import { Empty, Skeleton, Table } from 'antd';

const ThemeTable = ({
  columns,
  data,
  loader,
  pagination,
  onRow,
  scroll,
  skeletonRows = 5,
  emptyText,
  entityName = 'records',
}) => {
  // First load (or a load with nothing cached to show): render placeholder rows
  // so the table keeps its header/column widths instead of flashing "No Data".
  const showSkeleton = loader && !data?.length;

  const placeholderData = Array.from({ length: skeletonRows }, (_, index) => ({
    _id: `skeleton-${index}`,
  }));

  const placeholderColumns = columns?.map((column, index) => ({
    ...column,
    // minWidth: 0 is required — antd's `block` sets width:100% but leaves the
    // size preset's min-width (120px at size="small") in place, so without it
    // every placeholder cell floors at 120px and a wide table renders wider
    // while loading than it does with real data, flashing a horizontal
    // scrollbar that disappears once rows arrive.
    render: () => (
      <Skeleton.Input
        active
        block
        size="small"
        style={{ height: 16, minWidth: 0 }}
      />
    ),
    key: column.key || column.dataIndex || `skeleton-col-${index}`,
  }));

  return (
    <Table
      onRow={showSkeleton ? undefined : onRow}
      columns={showSkeleton ? placeholderColumns : columns}
      dataSource={showSkeleton ? placeholderData : data}
      rowKey={record => record._id || record.id || Math.random()}
      pagination={{
        defaultPageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '30'],
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} items`,
        // Keep a caller-supplied total during the skeleton. Forcing 0 here
        // combined with hideOnSinglePage removed the pager mid-load, so the
        // table shifted every time a page or filter changed.
        total: pagination?.total ?? (showSkeleton ? 0 : data?.length),
        current: pagination?.currentPage,
        hideOnSinglePage: true,
        // A page click while rows are loading would otherwise queue a second
        // request on top of the one in flight.
        disabled: showSkeleton,
        onChange: (page, pageSize) => {
          if (pagination) {
            pagination.setCurrentPage(page);
            pagination.setResultPerPage(pageSize);
          }
        },
      }}
      loading={showSkeleton ? false : loader}
      // Callers pass an `emptyText` describing what is missing and why — an
      // empty table is usually a filter that matched nothing, not an empty
      // account, and antd's bare "No Data" tells the admin neither. The
      // fallback still names the thing, so it never degrades to "No Data".
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={emptyText || `No ${entityName} to show`}
          />
        ),
      }}
      scroll={scroll}
    />
  );
};

export default ThemeTable;
